import { Menu, Table, Text, Tooltip } from '@mantine/core';
import {
	IconBuildingStore,
	IconInfoCircle,
	IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { query } from '../../../util/api/query';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { BranchDto } from '../../branches/types/BranchTypes';
import { BranchesModal } from '../../rear-display/components/BranchesModal';
import type { SupplierDto } from '../types/SupplierDto';
import { EditSupplierModal } from './EditSupplierModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface SupplierRowProps {
	initialSupplier: SupplierDto;
	refreshTable: () => void;
}

export function SupplierRow({
	initialSupplier,
	refreshTable,
}: SupplierRowProps) {
	const { organisation } = useParams();

	const [supplier, setSupplier] = useState(initialSupplier);

	const editModal = useModal();
	const branchesModal = useModal();
	const deleteModal = useModal();

	const queryBranches = query<BranchDto[]>(
		`${organisation}/supplier/branches/${supplier.id}`,
		branchesModal.isOpen,
	);

	const deleteMutation = mutate<BranchDto[]>(
		`${organisation}/supplier/delete/${supplier.id}`,
	);

	return (
		<>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Supplier'}
				itemDescription={supplier.name}
				mutation={deleteMutation}
				onComplete={refreshTable}
			/>
			<EditSupplierModal
				close={editModal.close}
				opened={editModal.isOpen}
				updateTableValue={setSupplier}
				initialSupplier={{
					id: supplier.id,
					accountNo: supplier.accountNo,
					address: supplier.address,
					branchIds: supplier.branchIds,
					county: supplier.county,
					email: supplier.email,
					name: supplier.name,
					phone: supplier.phone,
					postCode: supplier.postCode,
					ref: supplier.ref,
					town: supplier.town,
					website: supplier.website,
				}}
			/>
			<BranchesModal
				close={branchesModal.close}
				isOpened={branchesModal.isOpen}
				query={queryBranches}
			/>
			<KeyedRow key={supplier.id}>
				<Table.Td>{undefinedCheck(supplier.name)}</Table.Td>
				<Table.Td>{undefinedCheck(supplier.ref)}</Table.Td>
				<Table.Td>{undefinedCheck(supplier.address)}</Table.Td>
				<Table.Td>{undefinedCheck(supplier.town)}</Table.Td>
				<Table.Td>{undefinedCheck(supplier.email)}</Table.Td>
				<Table.Td>{undefinedCheck(supplier.phone)}</Table.Td>
				<Table.Td w={'8vw'}>
					<Tooltip
						label={supplier.website}
						disabled={!supplier.website || supplier.website === ''}
					>
						<Text size={'sm'} maw={'7vw'} lineClamp={1}>
							{undefinedCheck(supplier.website)}
						</Text>
					</Tooltip>
				</Table.Td>
				<Table.Td>{undefinedCheck(supplier.accountNo)}</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconInfoCircle {...defaultIconStyle} />}
							onClick={() => editModal.open()}
							data-cy={'edit-button'}
						>
							Edit
						</Menu.Item>
						<Menu.Item
							leftSection={<IconBuildingStore {...defaultIconStyle} />}
							onClick={() => branchesModal.open()}
						>
							View Branches
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
							data-cy={'delete-button'}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
		</>
	);
}
